import React, { useMemo, useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Divider,
  TableBody,
  TableContainer,
  TableCell,
  TableRow,
  Table,
  Switch,
  IconButton,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useFetch } from "services/hooks/useFetch";
import { CustomButtonWithBorder } from "components/CustomComponents/CustomButton";
import { customAPI } from "services/utils/CustomAPI";
import { customGetAPI } from "services/utils/CustomAPI";
import {
  parentBoxStyle,
  infoStyle,
  toAlignEnd,
  childBoxStyle,
} from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { CustomAutoComplete } from "components/CustomComponents";
import { CustomTextBox } from "components/CustomComponents";
import { onlyNumberRegex } from "services/constants/patterns";
import { SalaryComponentTable } from "pages/HR/SalaryConfiguration/Configuration/SalaryTemplate/SalaryComponentTable";
import { useSalaryComponents } from "pages/HR/SalaryConfiguration/Configuration/SalaryTemplate/useSalaryComponents";
import { childBoxStyleSpaceBetween } from "services/constants";
import { DoubleArrow, HelpCenter } from "@mui/icons-material";
import { CustomButton } from "components/CustomComponents";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import dayjs from "dayjs";
import { doubleArrowStyle } from "services/constants";

const updateCrossCategoryComponents = (
  changedComponent,
  allComponentsMap,
  totalSalary,
) => {
  const updatedCategories = {};
  let currentComponentsMap = { ...allComponentsMap };

  const getComponentValue = (code) => {
    if (!code) return 0;

    const upperCode = code.toUpperCase();

    if (upperCode === "GROSS") {
      return totalSalary;
    }

    for (const components of Object.values(updatedCategories)) {
      for (const component of components) {
        const componentCode =
          component.code?.toUpperCase() ||
          component.component_name?.code?.toUpperCase();

        if (componentCode === upperCode) {
          return parseFloat(component.amount) || 0;
        }
      }
    }

    for (const components of Object.values(currentComponentsMap)) {
      for (const component of components) {
        const componentCode =
          component.code?.toUpperCase() ||
          component.component_name?.code?.toUpperCase();

        if (componentCode === upperCode) {
          return parseFloat(component.amount) || 0;
        }
      }
    }

    return 0;
  };

  let hasChanges = true;
  let maxIterations = 10;
  let iteration = 0;

  while (hasChanges && iteration < maxIterations) {
    hasChanges = false;
    iteration++;

    for (const [category, components] of Object.entries(currentComponentsMap)) {
      const currentUpdated = updatedCategories[category] || [...components];
      const updatedComponents = [...currentUpdated];
      let categoryChanged = false;

      for (let i = 0; i < updatedComponents.length; i++) {
        const component = updatedComponents[i];
        const componentCode =
          component.code?.toUpperCase() ||
          component.component_name?.code?.toUpperCase();
        const changedCode =
          changedComponent.code?.toUpperCase() ||
          changedComponent.component_name?.code?.toUpperCase();

        if (componentCode === changedCode) {
          continue;
        }

        let newAmount = component.amount || 0;

        if (
          component.computation_method === "Percentage" &&
          component.value_based_on &&
          component.computation_value
        ) {
          const basedOn = Array.isArray(component.value_based_on)
            ? component.value_based_on
            : [component.value_based_on];

          let baseAmount = 0;
          basedOn.forEach((dep) => {
            baseAmount += getComponentValue(dep);
          });

          newAmount =
            (parseFloat(component.computation_value) / 100) * baseAmount;
        }

        if (
          component.computation_method === "Formula" &&
          component.value_based_formula
        ) {
          try {
            let expression = component.value_based_formula
              .toUpperCase()
              .replace(/\s+/g, "");
            const variableValues = {};
            const variableNames = expression.match(/\b[A-Z]+\b/g) || [];
            const uniqueVariables = [...new Set(variableNames)];

            uniqueVariables.forEach((variable) => {
              variableValues[variable] = getComponentValue(variable);
            });

            let evaluatedExpression = expression;
            Object.entries(variableValues).forEach(([key, value]) => {
              const regex = new RegExp(`\\b${key}\\b`, "g");
              evaluatedExpression = evaluatedExpression.replace(
                regex,
                value.toString(),
              );
            });

            const calculate = (expr) => {
              while (expr.includes("(")) {
                expr = expr.replace(/\(([^()]+)\)/g, (match, innerExpr) => {
                  return calculate(innerExpr);
                });
              }

              const mulDivRegex =
                /([+-]?\d*\.?\d+)\s*([*/])\s*([+-]?\d*\.?\d+)/;
              while (mulDivRegex.test(expr)) {
                expr = expr.replace(mulDivRegex, (match, a, op, b) => {
                  const numA = parseFloat(a);
                  const numB = parseFloat(b);
                  return op === "*" ? numA * numB : numA / numB;
                });
              }

              const addSubRegex =
                /([+-]?\d*\.?\d+)\s*([+-])\s*([+-]?\d*\.?\d+)/;
              while (addSubRegex.test(expr)) {
                expr = expr.replace(addSubRegex, (match, a, op, b) => {
                  const numA = parseFloat(a);
                  const numB = parseFloat(b);
                  return op === "+" ? numA + numB : numA - numB;
                });
              }

              return parseFloat(expr) || 0;
            };

            newAmount = calculate(evaluatedExpression);
          } catch (error) {
            showToast("error", "Error calculating formula:", error);
            newAmount = 0;
          }
        }

        if (
          (component.computation_method === "Percentage" ||
            component.computation_method === "Formula") &&
          Math.abs(newAmount - (component.amount || 0)) > 0.01
        ) {
          updatedComponents[i] = {
            ...component,
            amount: Number(newAmount.toFixed(2)),
          };
          categoryChanged = true;
          hasChanges = true;
        }
      }

      if (categoryChanged) {
        updatedCategories[category] = updatedComponents;
        currentComponentsMap[category] = updatedComponents;
      }
    }
  }

  return updatedCategories;
};

export const EmployeeSalaryInfo = ({
  onClose,
  reCallApi,
  isEditScreen = false,
  getCompanyId,
  isAddScreen = false,
  isSalaryTemplateUpdate = false,
  showBackArrow = false,
}) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const { userid } = useSelector((state) => state.hrportal);
  const [isNetSalaryNegative, setIsNetSalaryNegative] = useState(false);

  const {
    setValue,
    handleSubmit,
    register,
    watch,
    trigger,
    reset,
    control,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      payroll_group: "",
      template_name: "",
      salary_cal_type: "",
      salary: "",
      is_employer_contribution_allowed: false,
      is_reimbursement_allowed: false,
      trigger: false,
      is_override_template: false,
      get_template_id: true,
    },
  });

  const { result: payrollGrpData } = useFetch(
    `${domain}payroll/setup/payrollgroup/payroll-group/?company=${getCompanyId}`,
  );
  const { result: salaryTemplateData } = useFetch(
    `${domain}hrzone/salary/salary_template/salary-template-list/?company_id=${getCompanyId}`,
  );
  const { result: componentCategoryData } = useFetch(
    `${domain}hrzone/salary/salary_template/get-salary-type-choices/`,
  );
  const { result: userDetailsData } = useFetch(
    `${domain}employee/details?user_id=${userid}`,
  );
  const [templateData, setGetTemplateData] = useState("");
  const [isTemplateChanged, setIsTemplateChanged] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  const parseDate = (str) => {
    if (!str) return null;
    const [dd, mm, yyyy] = str.split("-");
    return new Date(yyyy, mm - 1, dd);
  };

  const allowPayrollGrp =
    parseDate(userDetailsData?.onboard_date) <=
    parseDate(userDetailsData?.last_pay_date);

  useEffect(() => {
    if (watch("get_template_id") && userDetailsData?.length !== 0) {
      setValue("get_template_id", false);
      setValue(
        "template_id",
        userDetailsData?.salary_template_data?.template_id,
      );
      setIsTemplateChanged(
        userDetailsData?.salary_template_data?.template_id ? true : false,
      );
    }
  }, [setValue, userDetailsData, watch]);

  useEffect(() => {
    const getTemplateDetails = async () => {
      const res = await customGetAPI(
        `${domain}hrzone/salary/salary_template/salary-template-list/?template_id=${watch(
          "template_id",
        )}`,
      );

      if (res.ok) {
        setGetTemplateData(res?.responseDetails);
        setValue("trigger", true);
        setIsTemplateChanged(false);
      } else {
        showToast("error", res?.message);
      }
    };

    if (watch("template_id") && isTemplateChanged) {
      getTemplateDetails();
    }
  }, [domain, isTemplateChanged, setValue, userDetailsData, watch]);

  const { result: computationMethodChoices } = useFetch(
    `${domain}HRZone/Salary/Salary_Component/get-computation-method-choices/`,
  );

  const { result: earningValue } = useFetch(
    `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=Earnings&company_id=${getCompanyId}`,
  );

  const { result: deductionValue } = useFetch(
    `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=Deduction&company_id=${getCompanyId}`,
  );

  const { result: employerContributionValue } = useFetch(
    `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=Employer Contribution&company_id=${getCompanyId}`,
  );

  const { result: reimbursementValue } = useFetch(
    `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=Reimbursement&company_id=${getCompanyId}`,
  );

  const totalSalary = parseFloat(watch("salary")) || 0;
  const earnings = useSalaryComponents("earnings", earningValue, totalSalary);
  const deductions = useSalaryComponents("deductions", deductionValue);
  const employerContributions = useSalaryComponents(
    "employer_contributions",
    employerContributionValue,
  );
  const reimbursements = useSalaryComponents(
    "reimbursements",
    reimbursementValue,
  );

  const totalEarnings = earnings.totalAmount;
  const totalDeductions = deductions.totalAmount;
  const totalEmployerContributions = employerContributions.totalAmount;
  const totalReimbursements = reimbursements.totalAmount;

  const salary = watch("salary");

  const netSalary = useMemo(() => {
    return (parseFloat(salary) || 0) - (parseFloat(totalDeductions) || 0);
  }, [salary, totalDeductions]);

  const totalCTC = useMemo(
    () => totalEarnings + totalEmployerContributions,
    [totalEarnings, totalEmployerContributions],
  );

  const parsePercentageValue = (str = "") => {
    if (!str || !/\bof\b/i.test(str)) {
      return { computation_value: "", value_based_on: [] };
    }

    const [valuePart, basePart] = str.split(/\bof\b/i);

    return {
      computation_value: valuePart.replace(/%/g, "").trim(),

      value_based_on: basePart
        .split("+")
        .map((v) => v.trim())
        .filter(Boolean),
    };
  };

  const prefillSalaryComponents = useCallback(
    (salaryData) => {
      if (salaryData.earnings && salaryData.earnings.length > 0) {
        const earningsData = salaryData.earnings.map((earning) => {
          const isPercentage = earning?.computation_method === "Percentage";
          const parsedPercentage = isPercentage
            ? parsePercentageValue(earning?.computation_value)
            : {};
          return {
            id: earning?.component_id,
            component_name: {
              label: earning?.component_name,
              id: earning?.component_id,
              code: earning?.component_code,
            },
            component_category: "Earnings",
            computation_method: earning?.computation_method,
            amount: earning?.amount || 0,
            code: earning?.component_code,
            isSpecialAllowance:
              earning.component_code === "SP" &&
              earning?.component_name === "Special Allowance",
            isBasic: earning.component_code === "BASIC",
            computation_value: isPercentage
              ? parsedPercentage.computation_value
              : earning?.computation_value,
            value_based_on: isPercentage
              ? parsedPercentage.value_based_on
              : earning?.value_based_on,
            value_based_formula: earning?.computation_value,
            ...(isPercentage && {
              value_based_percentage: earning?.computation_value,
            }),
          };
        });
        earnings.setComponents(earningsData);
      } else {
        earnings.setComponents([]);
      }

      if (salaryData.deduction && salaryData.deduction.length > 0) {
        const deductionsData = salaryData.deduction.map((deduction) => {
          const isPercentage = deduction?.computation_method === "Percentage";

          const parsedPercentage = isPercentage
            ? parsePercentageValue(deduction?.computation_value)
            : {};

          return {
            id: deduction?.component_id,
            component_name: {
              label: deduction?.component_name,
              id: deduction?.component_id,
              code: deduction?.component_code,
            },
            component_category: "Deduction",
            computation_method: deduction?.computation_method,
            amount: deduction?.amount || 0,
            code: deduction?.component_code,
            computation_value: isPercentage
              ? parsedPercentage.computation_value
              : deduction?.computation_value,
            value_based_on: isPercentage
              ? parsedPercentage.value_based_on
              : deduction?.value_based_on,
            value_based_formula: deduction?.computation_value,
            ...(isPercentage && {
              value_based_percentage: deduction?.computation_value,
            }),
          };
        });
        deductions.setComponents(deductionsData);
      } else {
        deductions.setComponents([]);
      }

      if (
        salaryData.employer_contribution &&
        salaryData.employer_contribution.length > 0
      ) {
        const employerContributionsData = salaryData.employer_contribution.map(
          (contribution) => {
            const isPercentage =
              contribution?.computation_method === "Percentage";

            const parsedPercentage = isPercentage
              ? parsePercentageValue(contribution?.computation_value)
              : {};

            return {
              id: contribution?.component_id,
              component_name: {
                label: contribution?.component_name,
                id: contribution?.component_id,
                code: contribution?.component_code,
              },
              component_category: "Employer Contribution",
              computation_method: contribution?.computation_method,
              amount: contribution?.amount || 0,
              code: contribution?.component_code,
              computation_value: isPercentage
                ? parsedPercentage.computation_value
                : contribution?.computation_value,
              value_based_on: isPercentage
                ? parsedPercentage.value_based_on
                : contribution?.value_based_on,
              value_based_formula: contribution?.computation_value,
              ...(isPercentage && {
                value_based_percentage: contribution?.computation_value,
              }),
            };
          },
        );
        employerContributions.setComponents(employerContributionsData);
      } else {
        employerContributions.setComponents([]);
      }

      if (salaryData.reimbursement && salaryData.reimbursement.length > 0) {
        const reimbursementsData = salaryData.reimbursement.map(
          (reimbursement) => ({
            id: reimbursement?.component_id,
            component_name: {
              label: reimbursement?.component_name,
              id: reimbursement?.component_id,
              code: reimbursement?.component_code,
            },
            component_category: "Reimbursement",
            computation_method: reimbursement?.computation_method,
            amount: reimbursement?.amount || 0,
            code: reimbursement?.component_code,
            computation_value: reimbursement?.computation_value,
            value_based_on: reimbursement?.value_based_on,
            value_based_formula: reimbursement?.value_based_formula,
          }),
        );
        reimbursements.setComponents(reimbursementsData);
      } else {
        reimbursements.setComponents([]);
      }
    },
    [earnings, deductions, employerContributions, reimbursements],
  );

  useEffect(() => {
    if (templateData && watch("trigger") && isAddScreen && isChanged) {
      reset({
        payroll_group:
          userDetailsData?.salary_template_data?.payroll_group_name ||
          watch("payroll_group"),
        payroll_group_id:
          userDetailsData?.salary_template_data?.payroll_group_id ||
          watch("payroll_group_id"),
        template_id: templateData.salary_template_id || watch("template_id"),
        effective_from: userDetailsData?.salary_template_data?.effective_from
          ? dayjs(
              userDetailsData?.salary_template_data?.effective_from
                .split("-")
                .reverse()
                .join("-"),
            )
          : null,
        template_name: templateData.template_name || "",
        salary_cal_type: templateData.salary_type || "",
        salary: templateData.salary || "",
        salary_basis: templateData.salary_basis || "",
        is_employer_contribution_allowed:
          templateData.is_employer_contribution || false,
        is_reimbursement_allowed: templateData.is_reimbursement || false,
        is_override_template: templateData.over_ride || false,
      });
      setValue("trigger", false);
      prefillSalaryComponents(templateData);
      setIsChanged(false);
    }
  }, [
    templateData,
    reset,
    setValue,
    prefillSalaryComponents,
    watch,
    userDetailsData?.salary_template_data?.payroll_group_name,
    userDetailsData?.salary_template_data?.payroll_group_id,
    userDetailsData?.salary_template_data?.effective_from,
    isAddScreen,
    isChanged,
  ]);

  useEffect(() => {
    if (
      !isAddScreen &&
      watch("trigger") &&
      userDetailsData?.salary_template_data &&
      !isChanged
    ) {
      const data = userDetailsData.salary_template_data;
      reset({
        payroll_group: data.payroll_group_name || watch("payroll_group"),
        payroll_group_id: data.payroll_group_id || watch("payroll_group_id"),
        template_id: data.template_id || watch("template_id"),

        effective_from: data.effective_from
          ? dayjs(data.effective_from.split("-").reverse().join("-"))
          : null,

        template_name: data.template_name || "",
        salary_cal_type: data.salary_type || "",
        salary: data.salary || "",
        salary_basis: data.salary_basis || "",

        is_employer_contribution_allowed:
          data.is_employer_contribution ?? false,
        is_reimbursement_allowed: data.is_reimbursement ?? false,
        is_override_template: data.over_ride ?? false,
      });
      setValue("trigger", false);
      prefillSalaryComponents(data);
    } else if (templateData && watch("trigger")) {
      reset({
        payroll_group:
          userDetailsData?.salary_template_data?.payroll_group_name ||
          watch("payroll_group"),
        payroll_group_id:
          userDetailsData?.salary_template_data?.payroll_group_id ||
          watch("payroll_group_id"),
        template_id: templateData.salary_template_id || watch("template_id"),
        effective_from: userDetailsData?.salary_template_data?.effective_from
          ? dayjs(
              userDetailsData?.salary_template_data?.effective_from
                .split("-")
                .reverse()
                .join("-"),
            )
          : null,
        template_name: templateData.template_name || "",
        salary_cal_type: templateData.salary_type || "",
        salary: templateData.salary || "",
        salary_basis: templateData.salary_basis || "",
        is_employer_contribution_allowed:
          templateData.is_employer_contribution || false,
        is_reimbursement_allowed: templateData.is_reimbursement || false,
        is_override_template: templateData.over_ride || false,
      });
      setValue("trigger", false);
      prefillSalaryComponents(templateData);
    }
  }, [
    isAddScreen,
    watch,
    reset,
    setValue,
    prefillSalaryComponents,
    userDetailsData.salary_template_data,
    templateData,
    isChanged,
  ]);

  useEffect(() => {
    if (earnings.components.length > 0) {
      const specialAllowance = earnings.components.find(
        (comp) => comp.isSpecialAllowance,
      );
      if (specialAllowance) {
        const totalOtherEarnings = earnings.components.reduce((sum, comp) => {
          if (!comp.isSpecialAllowance && comp.amount) {
            return sum + parseFloat(comp.amount);
          }
          return sum;
        }, 0);

        const newSpecialAllowance = Math.max(
          0,
          totalSalary - totalOtherEarnings,
        );

        if (specialAllowance.amount !== newSpecialAllowance) {
          const updatedComponents = [...earnings.components];
          const specialIndex = updatedComponents.findIndex(
            (comp) => comp.isSpecialAllowance,
          );
          updatedComponents[specialIndex] = {
            ...updatedComponents[specialIndex],
            amount: newSpecialAllowance,
          };
          earnings.setComponents(updatedComponents);

          setTimeout(() => {
            const allComponentsMap = {
              earnings: updatedComponents,
              deductions: deductions.components,
              employer_contributions: employerContributions.components,
              reimbursements: reimbursements.components,
            };

            const updatedCategories = updateCrossCategoryComponents(
              updatedComponents[specialIndex],
              allComponentsMap,
              totalSalary,
            );

            if (updatedCategories.deductions) {
              deductions.setComponents(updatedCategories.deductions);
            }
            if (updatedCategories.employer_contributions) {
              employerContributions.setComponents(
                updatedCategories.employer_contributions,
              );
            }
            if (updatedCategories.reimbursements) {
              reimbursements.setComponents(updatedCategories.reimbursements);
            }
          }, 0);
        }
      }
    }
  }, [
    totalSalary,
    earnings,
    deductions,
    employerContributions,
    reimbursements,
  ]);

  const validateForm = async () => {
    const isFormValid = await trigger([
      "template_name",
      "salary_type",
      "salary",
      "rounding_rule",
      "rounding_value",
    ]);

    const isEarningsValid = earnings.validateComponents();
    const isDeductionsValid = deductions.validateComponents();
    const isEmployerContributionsValid =
      employerContributions.validateComponents();
    const isReimbursementsValid = reimbursements.validateComponents();

    return (
      isFormValid &&
      isEarningsValid &&
      isDeductionsValid &&
      isEmployerContributionsValid &&
      isReimbursementsValid
    );
  };

  const onSubmit = async (data) => {
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }
    if (netSalary < 0) {
      setIsNetSalaryNegative(true);
      return;
    }
    setValue("button_loader", true);
    const requestData = {
      ...(isEditScreen && {
        structure_id: userDetailsData?.salary_template_data?.structure_id ?? "",
      }),
      user_id: userid,
      payroll_group_id: data?.payroll_group_id,
      salary_template_id: data?.template_id,
      over_ride: data?.is_override_template,
      salary: data?.salary,
      effective_from: data?.effective_from.format("YYYY-MM-DD"),
      total_salary: totalEarnings,
      total_earnings: totalEarnings,
      total_deductions: totalDeductions,
      net_salary: netSalary,
      total_employer_contribution: totalEmployerContributions,
      total_ctc: totalCTC,
      total_reimbursement: totalReimbursements,
      is_employer_contribution: data?.is_employer_contribution_allowed,
      is_reimbursement: data?.is_reimbursement_allowed,
      salary_basis: data?.salary_basis,
      salary_type: data?.salary_cal_type?.label ?? data?.salary_cal_type,
      components: [
        earnings.prepareSubmissionData(),
        deductions.prepareSubmissionData(),
        employerContributions.prepareSubmissionData(),
        reimbursements.prepareSubmissionData(),
      ]
        .flat()
        .filter(Boolean),
    };

    const method =
      userDetailsData?.salary_template_data?.structure_id &&
      userDetailsData?.salary_template_data
        ? "PUT"
        : "POST";
    const url = `${domain}employee-salary-template/`;

    const response = await customAPI(url, method, requestData);

    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      setValue("button_loader", false);
      setIsNetSalaryNegative(false);
      if (
        data?.is_override_template &&
        response?.responseDetails?.structure_id
      ) {
        HanldePUTApi(data, response?.responseDetails?.structure_id);
      } else {
        onClose();
        reCallApi(true);
      }
    } else {
      setValue("button_loader", false);
      showToast("error", response?.message);
    }
  };

  const HanldePUTApi = async (data, structureID) => {
    const requestData = {
      structure_id: structureID,
      user_id: userid,
      payroll_group_id: data?.payroll_group_id,
      salary_template_id: data?.template_id,
      over_ride: data?.is_override_template,
      salary: data?.salary,
      effective_from: data?.effective_from.format("YYYY-MM-DD"),
      total_salary: totalEarnings,
      total_deductions: totalDeductions,
      net_salary: netSalary,
      total_employer_contribution: totalEmployerContributions,
      total_ctc: totalCTC,
      total_reimbursement: totalReimbursements,
      is_employer_contribution: data?.is_employer_contribution_allowed,
      is_reimbursement: data?.is_reimbursement_allowed,
      salary_basis: data?.salary_basis,
      salary_type: data?.salary_cal_type?.label,
      components: [
        earnings.prepareSubmissionData(),
        deductions.prepareSubmissionData(),
        employerContributions.prepareSubmissionData(),
        reimbursements.prepareSubmissionData(),
      ]
        .flat()
        .filter(Boolean),
    };
    const response = await customAPI(
      `${domain}employee-salary-template/`,
      "PUT",
      requestData,
    );
    if (response.ok) {
      onClose();
      reCallApi(true);
    } else {
      showToast("error", response?.message);
    }
  };

  const hanldePayrollGrpChange = (event, data) => {
    if (data) {
      setValue("payroll_group", data?.name);
      setValue("payroll_group_id", data?.id);
    } else {
      setValue("payroll_group", "");
      setValue("payroll_group_id", data?.id);
    }
  };

  const hanldeTemplateName = (event, data) => {
    if (data) {
      setValue("template_name", data?.template_name);
      setValue("template_id", data?.id);
      setIsTemplateChanged(true);
      setIsChanged(true);
    } else {
      setValue("template_name", "");
      setValue("template_id", "");
      setIsTemplateChanged(false);
    }
  };

  const handleCalType = (event, data) => {
    if (data) {
      setValue("salary_cal_type", data);
    } else {
      setValue("salary_cal_type", "");
    }
  };

  const handleAmount = (event) => {
    if (
      event.target.value?.length <= 8 &&
      onlyNumberRegex.test(event.target.value)
    ) {
      const newSalary = event.target.value;
      setValue("salary", newSalary);
      setTimeout(() => {
        if (earnings.components.length > 0) {
          const grossComponent = {
            code: "GROSS",
            component_name: { code: "GROSS" },
            amount: parseFloat(newSalary) || 0,
            computation_method: "Fixed Amount",
          };

          const allComponentsMap = {
            earnings: earnings.components,
            deductions: deductions.components,
            employer_contributions: employerContributions.components,
            reimbursements: reimbursements.components,
          };

          const updatedCategories = updateCrossCategoryComponents(
            grossComponent,
            allComponentsMap,
            parseFloat(newSalary) || 0,
          );
          if (updatedCategories.earnings) {
            earnings.setComponents(updatedCategories.earnings);
          }
          if (updatedCategories.deductions) {
            deductions.setComponents(updatedCategories.deductions);
          }
          if (updatedCategories.employer_contributions) {
            employerContributions.setComponents(
              updatedCategories.employer_contributions,
            );
          }
          if (updatedCategories.reimbursements) {
            reimbursements.setComponents(updatedCategories.reimbursements);
          }
        }
      }, 100);
    } else {
      setValue("salary", "");
    }
  };

  const handleEarningsChangeWithRecalc = useCallback(
    (index, type, value) => {
      if (type === "name") {
        earnings.handleComponentNameChange(
          index,
          value,
          earnings.setComponents,
        );
      } else if (type === "method") {
        earnings.handleComputationMethodChange(
          index,
          value,
          earnings.setComponents,
        );
      } else if (type === "amount") {
        earnings.handleAmountChange(index, value, earnings.setComponents);
      }

      setTimeout(() => {
        const changedComponent = earnings.components[index];
        if (changedComponent) {
          const allComponentsMap = {
            earnings: earnings.components,
            deductions: deductions.components,
            employer_contributions: employerContributions.components,
            reimbursements: reimbursements.components,
          };

          const updatedCategories = updateCrossCategoryComponents(
            changedComponent,
            allComponentsMap,
            totalSalary,
          );

          if (updatedCategories.deductions) {
            deductions.setComponents(updatedCategories.deductions);
          }
          if (updatedCategories.employer_contributions) {
            employerContributions.setComponents(
              updatedCategories.employer_contributions,
            );
          }
          if (updatedCategories.reimbursements) {
            reimbursements.setComponents(updatedCategories.reimbursements);
          }
          if (updatedCategories.earnings) {
            earnings.setComponents(updatedCategories.earnings);
          }
        }
      }, 0);
    },
    [earnings, deductions, employerContributions, reimbursements, totalSalary],
  );

  const handleDeductionsChangeWithRecalc = useCallback(
    (index, type, value) => {
      if (type === "name") {
        deductions.handleComponentNameChange(
          index,
          value,
          deductions.setComponents,
        );
      } else if (type === "method") {
        deductions.handleComputationMethodChange(
          index,
          value,
          deductions.setComponents,
        );
      } else if (type === "amount") {
        deductions.handleAmountChange(index, value, deductions.setComponents);
      }

      setTimeout(() => {
        const changedComponent = deductions.components[index];
        if (changedComponent) {
          const allComponentsMap = {
            earnings: earnings.components,
            deductions: deductions.components,
            employer_contributions: employerContributions.components,
            reimbursements: reimbursements.components,
          };

          const updatedCategories = updateCrossCategoryComponents(
            changedComponent,
            allComponentsMap,
            totalSalary,
          );

          if (updatedCategories.earnings) {
            earnings.setComponents(updatedCategories.earnings);
          }
          if (updatedCategories.employer_contributions) {
            employerContributions.setComponents(
              updatedCategories.employer_contributions,
            );
          }
          if (updatedCategories.reimbursements) {
            reimbursements.setComponents(updatedCategories.reimbursements);
          }
        }
      }, 0);
    },
    [deductions, earnings, employerContributions, reimbursements, totalSalary],
  );

  const handleEmployerContributionsChangeWithRecalc = useCallback(
    (index, type, value) => {
      if (type === "name") {
        employerContributions.handleComponentNameChange(
          index,
          value,
          employerContributions.setComponents,
        );
      } else if (type === "method") {
        employerContributions.handleComputationMethodChange(
          index,
          value,
          employerContributions.setComponents,
        );
      } else if (type === "amount") {
        employerContributions.handleAmountChange(
          index,
          value,
          employerContributions.setComponents,
        );
      }

      setTimeout(() => {
        const changedComponent = employerContributions.components[index];
        if (changedComponent) {
          const allComponentsMap = {
            earnings: earnings.components,
            deductions: deductions.components,
            employer_contributions: employerContributions.components,
            reimbursements: reimbursements.components,
          };

          const updatedCategories = updateCrossCategoryComponents(
            changedComponent,
            allComponentsMap,
            totalSalary,
          );

          if (updatedCategories.earnings) {
            earnings.setComponents(updatedCategories.earnings);
          }
          if (updatedCategories.deductions) {
            deductions.setComponents(updatedCategories.deductions);
          }
          if (updatedCategories.reimbursements) {
            reimbursements.setComponents(updatedCategories.reimbursements);
          }
        }
      }, 0);
    },
    [employerContributions, earnings, deductions, reimbursements, totalSalary],
  );

  const handleReimbursementsChangeWithRecalc = useCallback(
    (index, type, value) => {
      if (type === "name") {
        reimbursements.handleComponentNameChange(
          index,
          value,
          reimbursements.setComponents,
        );
      } else if (type === "method") {
        reimbursements.handleComputationMethodChange(
          index,
          value,
          reimbursements.setComponents,
        );
      } else if (type === "amount") {
        reimbursements.handleAmountChange(
          index,
          value,
          reimbursements.setComponents,
        );
      }

      setTimeout(() => {
        const changedComponent = reimbursements.components[index];
        if (changedComponent) {
          const allComponentsMap = {
            earnings: earnings.components,
            deductions: deductions.components,
            employer_contributions: employerContributions.components,
            reimbursements: reimbursements.components,
          };

          const updatedCategories = updateCrossCategoryComponents(
            changedComponent,
            allComponentsMap,
            totalSalary,
          );

          if (updatedCategories.earnings) {
            earnings.setComponents(updatedCategories.earnings);
          }
          if (updatedCategories.deductions) {
            deductions.setComponents(updatedCategories.deductions);
          }
          if (updatedCategories.employer_contributions) {
            employerContributions.setComponents(
              updatedCategories.employer_contributions,
            );
          }
        }
      }, 0);
    },
    [reimbursements, earnings, deductions, employerContributions, totalSalary],
  );

  const buildSpecialAllowanceFormula = (components) => {
    const deductionCodes = components
      .filter(
        (comp) =>
          !comp.isSpecialAllowance &&
          comp.code &&
          comp.amount !== undefined &&
          comp.amount !== null &&
          !Number.isNaN(Number(comp.amount)),
      )
      .map((comp) => comp.code);

    return deductionCodes.length
      ? `GROSS - ${[...deductionCodes].reverse().join(" - ")}`
      : "GROSS";
  };

  const handleRemoveComponentWithRecalc = useCallback(
    (category, id, index) => {
      let removedComponent = null;
      let updatedComponents = [];

      if (category === "earnings") {
        removedComponent = earnings.components[index];
        updatedComponents = earnings.components.filter(
          (item, i) => i !== index,
        );
        const formula = buildSpecialAllowanceFormula(updatedComponents);
        const updatedComponent = updatedComponents.map((item, i) => {
          if (item?.code === "SP") {
            return {
              ...item,
              value_based_formula: formula,
            };
          }
          return item;
        });
        earnings.setComponents(updatedComponent);
      } else if (category === "deductions") {
        removedComponent = deductions.components[index];
        updatedComponents = deductions.components.filter(
          (item, i) => i !== index,
        );
        deductions.setComponents(updatedComponents);
      } else if (category === "employer_contributions") {
        removedComponent = employerContributions.components[index];
        updatedComponents = employerContributions.components.filter(
          (item, i) => i !== index,
        );
        employerContributions.setComponents(updatedComponents);
      } else {
        removedComponent = reimbursements.components[index];
        updatedComponents = reimbursements.components.filter(
          (item, i) => i !== index,
        );
        reimbursements.setComponents(updatedComponents);
      }

      if (removedComponent) {
        setTimeout(() => {
          const allComponentsMap = {
            earnings:
              category === "earnings" ? updatedComponents : earnings.components,
            deductions:
              category === "deductions"
                ? updatedComponents
                : deductions.components,
            employer_contributions:
              category === "employer_contributions"
                ? updatedComponents
                : employerContributions.components,
            reimbursements:
              category === "reimbursements"
                ? updatedComponents
                : reimbursements.components,
          };

          const updatedCategories = updateCrossCategoryComponents(
            removedComponent,
            allComponentsMap,
            totalSalary,
          );

          if (updatedCategories.earnings && category !== "earnings") {
            earnings.setComponents(updatedCategories.earnings);
          }
          if (updatedCategories.deductions && category !== "deductions") {
            deductions.setComponents(updatedCategories.deductions);
          }
          if (
            updatedCategories.employer_contributions &&
            category !== "employer_contributions"
          ) {
            employerContributions.setComponents(
              updatedCategories.employer_contributions,
            );
          }
          if (
            updatedCategories.reimbursements &&
            category !== "reimbursements"
          ) {
            reimbursements.setComponents(updatedCategories.reimbursements);
          }
        }, 0);
      }
    },
    [earnings, deductions, employerContributions, reimbursements, totalSalary],
  );

  const updatePercentageDisplay = (component) => {
    if (!component || component.computation_method !== "Percentage") {
      return component;
    }

    const enhanced = { ...component };

    if (enhanced.computation_value && enhanced.value_based_on) {
      const basedOn = Array.isArray(enhanced.value_based_on)
        ? enhanced.value_based_on
        : [enhanced.value_based_on];

      enhanced.value_based_percentage = `${
        enhanced.computation_value
      }% of ${basedOn.join(" + ")}`;
    } else {
      enhanced.value_based_percentage = "";
    }

    return enhanced;
  };

  const handleComponentUpdate = useCallback(
    (category, index, updatedComponent) => {
      let updatedComponents;
      let allComponentsMap;

      const enhancedComponent = updatePercentageDisplay(updatedComponent);

      if (category === "earnings") {
        updatedComponents = [...earnings.components];
        updatedComponents[index] = enhancedComponent;
        earnings.setComponents(updatedComponents);

        allComponentsMap = {
          earnings: updatedComponents,
          deductions: deductions.components,
          employer_contributions: employerContributions.components,
          reimbursements: reimbursements.components,
        };
      } else if (category === "deductions") {
        updatedComponents = [...deductions.components];
        updatedComponents[index] = enhancedComponent;
        deductions.setComponents(updatedComponents);

        allComponentsMap = {
          earnings: earnings.components,
          deductions: updatedComponents,
          employer_contributions: employerContributions.components,
          reimbursements: reimbursements.components,
        };
      } else if (category === "employer_contributions") {
        updatedComponents = [...employerContributions.components];
        updatedComponents[index] = enhancedComponent;
        employerContributions.setComponents(updatedComponents);

        allComponentsMap = {
          earnings: earnings.components,
          deductions: deductions.components,
          employer_contributions: updatedComponents,
          reimbursements: reimbursements.components,
        };
      } else {
        return;
      }

      setTimeout(() => {
        const updatedCategories = updateCrossCategoryComponents(
          updatedComponent,
          allComponentsMap,
          totalSalary,
        );

        if (updatedCategories.earnings) {
          earnings.setComponents(updatedCategories.earnings);
        }
        if (updatedCategories.deductions) {
          deductions.setComponents(updatedCategories.deductions);
        }
        if (updatedCategories.employer_contributions) {
          employerContributions.setComponents(
            updatedCategories.employer_contributions,
          );
        }
        if (updatedCategories.reimbursements) {
          reimbursements.setComponents(updatedCategories.reimbursements);
        }
      }, 0);
    },
    [earnings, deductions, employerContributions, reimbursements, totalSalary],
  );

  return (
    <Box sx={parentBoxStyle}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          {showBackArrow && (
            <Box mb={1}>
              <IconButton
                onClick={onClose}
                sx={{ ...doubleArrowStyle, paddingLeft: 2 }}
              >
                <DoubleArrow />
              </IconButton>
            </Box>
          )}

          <Box sx={childBoxStyle}>
            <Box
              sx={{
                pointerEvents: isSalaryTemplateUpdate
                  ? "auto"
                  : allowPayrollGrp
                    ? "auto"
                    : "none",
                opacity: isSalaryTemplateUpdate ? 1 : allowPayrollGrp ? 1 : 0.8,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Payroll Group</Typography>
              <Typography {...infoStyle}>
                Select the payroll group this template belongs to.
              </Typography>

              <CustomAutoComplete
                dataList={payrollGrpData?.map((obj) => ({
                  ...obj,
                  label: obj?.name,
                }))}
                rules={{
                  ...register("payroll_group", {
                    required: "Payroll group is required",
                  }),
                }}
                selectedValue={watch("payroll_group")}
                updateDataFunction={hanldePayrollGrpChange}
                placeHolder="Select payroll group name"
                errorMessage={
                  errors?.payroll_group?.message
                    ? errors?.payroll_group?.message
                    : ""
                }
                textBoxError={errors?.payroll_group ? true : false}
              />
            </Box>

            <Box
              sx={{
                pointerEvents: isSalaryTemplateUpdate ? "none" : "auto",
                opacity: isSalaryTemplateUpdate ? 0.8 : 1,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Template Name</Typography>
              <Typography {...infoStyle}>
                Select the predefined salary template to apply for the employee.
              </Typography>
              <CustomAutoComplete
                dataList={salaryTemplateData?.map((obj) => ({
                  ...obj,
                  label: obj?.template_name,
                }))}
                rules={{
                  ...register("template_name", {
                    required: "Template name is required",
                  }),
                }}
                selectedValue={watch("template_name")}
                updateDataFunction={hanldeTemplateName}
                placeHolder="Select template name"
                errorMessage={
                  errors?.template_name?.message
                    ? errors?.template_name?.message
                    : ""
                }
                textBoxError={errors?.template_name ? true : false}
              />
            </Box>

            <Box
              sx={{
                pointerEvents: isSalaryTemplateUpdate ? "none" : "auto",
                opacity: isSalaryTemplateUpdate ? 0.8 : 1,
                mb: 2,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Effective From</Typography>
              <Typography {...infoStyle} sx={{ mb: -0.5 }}>
                Choose the effective from date.
              </Typography>
              <CustomizedDatePicker
                name="effective_from"
                control={control}
                required={true}
                requiredMessage="Effective from is required"
                placeholder="Select effective from date"
                startDate={watch("effective_from")}
              />
            </Box>

            <Box>
              <Box
                sx={{ ...childBoxStyleSpaceBetween, px: 0, borderRadius: 1 }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 20 }}>
                  Override Template
                </Typography>
                <Switch
                  checked={watch("is_override_template")}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setValue("is_override_template", checked);
                    // if (!checked) {
                    //   setValue("trigger", true);
                    // }
                  }}
                  sx={{ transform: "scale(1.3)" }}
                />
              </Box>
              <Box sx={{ backgroundColor: "#fffde4" }}>
                <Typography
                  {...infoStyle}
                  sx={{
                    p: 2,
                    mt: 0.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <HelpCenter color="primary" />
                  When Override is turned on, you can manually edit salary
                  values. The modified template will be saved as a Custom
                  template.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                pointerEvents: !watch("is_override_template") ? "none" : "auto",
                opacity: !watch("is_override_template") ? 0.8 : 1,
                ...childBoxStyle,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  Salary Calculation Type
                </Typography>
                <Typography {...infoStyle}>
                  Choose the type of salary structure.
                </Typography>
                <CustomAutoComplete
                  dataList={componentCategoryData}
                  rules={{
                    ...register("salary_cal_type", {
                      required: "Salary calculation type is required",
                    }),
                  }}
                  selectedValue={watch("salary_cal_type")}
                  updateDataFunction={handleCalType}
                  placeHolder="Select salary calculation type"
                  errorMessage={
                    errors?.salary_cal_type?.message
                      ? errors?.salary_cal_type?.message
                      : ""
                  }
                  textBoxError={errors?.salary_cal_type ? true : false}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Salary Basis</Typography>
                  <Typography {...infoStyle}>
                    Enter the employee’s fixed gross salary amount.
                  </Typography>
                  <CustomTextBox
                    selectedValue={watch("salary_basis")}
                    disabledValue={true}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Amount</Typography>
                  <Typography {...infoStyle}>
                    Enter the employee’s fixed gross salary amount.
                  </Typography>
                  <CustomTextBox
                    selectedValue={watch("salary")}
                    rules={{
                      ...register("salary", {
                        required: "Salary is required",
                        validate: (value) => {
                          if (value && parseFloat(value) <= 0) {
                            return "Salary must be greater than 0";
                          }
                          return true;
                        },
                      }),
                    }}
                    updateDataFunction={handleAmount}
                    placeHolder="Enter amount"
                    errorMessage={
                      errors?.salary?.message ? errors?.salary?.message : ""
                    }
                    textBoxError={errors.salary ? true : false}
                  />
                </Box>
              </Box>

              <SalaryComponentTable
                title="Earnings"
                description="Add components that contribute to employee earnings (Basic, HRA, Bonus, etc.)"
                data={earnings.components}
                earningValue={[
                  ...earnings?.components.slice(0, -1),
                  { label: "GROSS", code: "GROSS", amount: totalSalary },
                ]}
                is_earning={true}
                setData={earnings.setComponents}
                errors={earnings.errors}
                setErrors={earnings.setErrors}
                componentList={earningValue?.data || []}
                computationMethodChoices={computationMethodChoices || []}
                filteredComponentLists={earnings.filteredComponentLists}
                totalLabel="Total Earnings"
                totalSalary={totalSalary}
                onAddComponent={() => earnings.addComponent()}
                onRemoveComponent={(id, index) =>
                  handleRemoveComponentWithRecalc("earnings", id, index)
                }
                onComponentNameChange={(index, value) =>
                  handleEarningsChangeWithRecalc(index, "name", value)
                }
                onComputationMethodChange={(index, value) =>
                  handleEarningsChangeWithRecalc(index, "method", value)
                }
                onAmountChange={(index, event) =>
                  handleEarningsChangeWithRecalc(index, "amount", event)
                }
                onComponentUpdate={(index, updatedComponent) =>
                  handleComponentUpdate("earnings", index, updatedComponent)
                }
              />

              <Divider />

              <SalaryComponentTable
                title="Deductions"
                description="Add components that are deducted from earnings (TDS, PF, Loan, etc.)"
                data={deductions.components}
                earningValue={[
                  ...earnings?.components,
                  { label: "GROSS", code: "GROSS", amount: totalSalary },
                ]}
                setData={deductions.setComponents}
                errors={deductions.errors}
                setErrors={deductions.setErrors}
                componentList={deductionValue?.data || []}
                computationMethodChoices={computationMethodChoices || []}
                filteredComponentLists={deductions.filteredComponentLists}
                totalLabel="Total Deductions"
                totalSalary={totalSalary}
                onAddComponent={() => deductions.addComponent()}
                onRemoveComponent={(id, index) =>
                  handleRemoveComponentWithRecalc("deductions", id, index)
                }
                onComponentNameChange={(index, value) =>
                  handleDeductionsChangeWithRecalc(index, "name", value)
                }
                onComputationMethodChange={(index, value) =>
                  handleDeductionsChangeWithRecalc(index, "method", value)
                }
                onAmountChange={(index, event) =>
                  handleDeductionsChangeWithRecalc(index, "amount", event)
                }
                onComponentUpdate={(index, updatedComponent) =>
                  handleComponentUpdate("deductions", index, updatedComponent)
                }
              />

              <Divider />

              <Box sx={{ ...childBoxStyleSpaceBetween, px: 0 }}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                    Employer Contribution
                  </Typography>
                  <Typography {...infoStyle}>
                    Include employer contribution component in salary structure
                  </Typography>
                </Box>
                <Switch
                  checked={watch("is_employer_contribution_allowed")}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setValue("is_employer_contribution_allowed", checked);
                  }}
                  sx={{ transform: "scale(1.3)" }}
                />
              </Box>

              {watch("is_employer_contribution_allowed") && (
                <SalaryComponentTable
                  title="Employer Contribution"
                  description="Add statutory or custom employer contributions (PF,ESI,Insurance,etc."
                  data={employerContributions.components}
                  earningValue={[
                    ...earnings?.components,
                    { label: "GROSS", code: "GROSS", amount: totalSalary },
                  ]}
                  setData={employerContributions.setComponents}
                  errors={employerContributions.errors}
                  setErrors={employerContributions.setErrors}
                  componentList={employerContributionValue?.data || []}
                  computationMethodChoices={computationMethodChoices || []}
                  filteredComponentLists={
                    employerContributions.filteredComponentLists
                  }
                  totalLabel="Total Contribution"
                  totalSalary={totalSalary}
                  onAddComponent={() => employerContributions.addComponent()}
                  onRemoveComponent={(id, index) =>
                    handleRemoveComponentWithRecalc(
                      "employer_contributions",
                      id,
                      index,
                    )
                  }
                  onComponentNameChange={(index, value) =>
                    handleEmployerContributionsChangeWithRecalc(
                      index,
                      "name",
                      value,
                    )
                  }
                  onComputationMethodChange={(index, value) =>
                    handleEmployerContributionsChangeWithRecalc(
                      index,
                      "method",
                      value,
                    )
                  }
                  onAmountChange={(index, event) =>
                    handleEmployerContributionsChangeWithRecalc(
                      index,
                      "amount",
                      event,
                    )
                  }
                  onComponentUpdate={(index, updatedComponent) =>
                    handleComponentUpdate(
                      "employer_contributions",
                      index,
                      updatedComponent,
                    )
                  }
                />
              )}

              <Divider />

              <Box sx={{ ...childBoxStyleSpaceBetween, px: 0 }}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                    Reimbursement
                  </Typography>
                  <Typography {...infoStyle}>
                    Include reimbursement components in salary structure
                  </Typography>
                </Box>
                <Switch
                  checked={watch("is_reimbursement_allowed")}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setValue("is_reimbursement_allowed", checked);
                  }}
                  sx={{ transform: "scale(1.3)" }}
                />
              </Box>

              {watch("is_reimbursement_allowed") && (
                <SalaryComponentTable
                  title="Reimbursement"
                  description="Add reimbursement components for employee expenses (Travel,Food,Medical, etc.)"
                  data={reimbursements.components}
                  earningValue={[
                    ...earnings?.components,
                    { label: "GROSS", code: "GROSS", amount: totalSalary },
                  ]}
                  setData={reimbursements.setComponents}
                  errors={reimbursements.errors}
                  setErrors={reimbursements.setErrors}
                  componentList={reimbursementValue?.data || []}
                  computationMethodChoices={(
                    computationMethodChoices || []
                  ).filter((item) => item.label === "Fixed Amount")}
                  filteredComponentLists={reimbursements.filteredComponentLists}
                  totalLabel="Total Reimbursement"
                  onAddComponent={() => reimbursements.addComponent()}
                  onRemoveComponent={(id, index) =>
                    handleRemoveComponentWithRecalc("reimbursements", id, index)
                  }
                  onComponentNameChange={(index, value) =>
                    handleReimbursementsChangeWithRecalc(index, "name", value)
                  }
                  onComputationMethodChange={(index, value) =>
                    handleReimbursementsChangeWithRecalc(index, "method", value)
                  }
                  onAmountChange={(index, event) =>
                    handleReimbursementsChangeWithRecalc(index, "amount", event)
                  }
                  onComponentUpdate={(index, updatedComponent) =>
                    handleComponentUpdate(
                      "reimbursements",
                      index,
                      updatedComponent,
                    )
                  }
                />
              )}
              <Box
                sx={{
                  mt: 3,
                  p: 3,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>Summary</Typography>
                <Typography {...infoStyle}>
                  Add all componets that reduce the employees salary
                </Typography>

                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ border: "none", py: 1 }}
                        ></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 800,
                            border: "none",
                            pl: 0,
                            fontSize: "1.2rem",
                            color: "#2e7d32",
                          }}
                        >
                          Total Cost to Company (CTC)
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "none",
                            fontWeight: 800,
                            fontSize: "1.2rem",
                            color: "#2e7d32",
                          }}
                          align="right"
                        >
                          {totalCTC.toFixed(2)}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 600, border: "none", pl: 0 }}
                        >
                          Employer Contribution
                        </TableCell>
                        <TableCell sx={{ border: "none" }} align="right">
                          {totalEmployerContributions.toFixed(2)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 600, border: "none", pl: 0 }}
                        >
                          Gross Salary
                        </TableCell>
                        <TableCell sx={{ border: "none" }} align="right">
                          {totalEarnings.toFixed(2)}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 600, border: "none", pl: 0 }}
                        >
                          Deductions
                        </TableCell>
                        <TableCell sx={{ border: "none" }} align="right">
                          {totalDeductions.toFixed(2)}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={2} sx={{ border: "none", py: 1 }}>
                          <Divider />
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            border: "none",
                            pl: 0,
                            fontSize: "1.1rem",
                          }}
                        >
                          Net Salary
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "none",
                            fontWeight: 700,
                            fontSize: "1.1rem",
                          }}
                          align="right"
                        >
                          {netSalary.toFixed(2)}
                          {isNetSalaryNegative && (
                            <Typography
                              color="error"
                              variant="caption"
                              sx={{ display: "block", mt: 0.5 }}
                            >
                              {"Net salary cannot be negative"}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
            <Box sx={toAlignEnd}>
              <CustomButtonWithBorder
                actionName="Cancel"
                typeName="button"
                actionFuntion={() => onClose()}
              />
              <CustomButton
                actionName={isEditScreen ? "Update" : "Save"}
                typeName="submit"
                disableAction={watch("button_loader")}
              />
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};
